import { useState, useEffect } from 'react';
import {
    PersonneMorale,
    Parrainage,
    getParrainagesByPersonneMorale,
    deaffectParrainage,
} from '@app/api/data/personne-morales';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Card,
    Typography,
    Table,
    Sheet,
    Alert,
    Divider,
    Button,
    Snackbar,
} from '@mui/joy';
import AppLoading from '../../../components/AppLoading';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface ParrainagesPersonneMoraleProps {
    personneMorale?: PersonneMorale;
}

const ParrainagesPersonneMorale: React.FC<ParrainagesPersonneMoraleProps> = ({
    personneMorale,
}) => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [parrainages, setParrainages] = useState<Parrainage[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [deaffectingId, setDeaffectingId] = useState<number | null>(null);

    const fetchParrainages = async () => {
        if (!id) return;

        setLoading(true);
        try {
            const data = await getParrainagesByPersonneMorale(parseInt(id));
            setParrainages(data);
        } catch (err) {
            setError('Erreur lors du chargement des parrainages');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchParrainages();
    }, [id]);

    const handleDeaffect = async (parrainageId: number) => {
        setDeaffectingId(parrainageId);
        try {
            await deaffectParrainage(parrainageId);
            setSuccess('Parrainage désaffecté avec succès');
            // Rafraîchir la liste
            fetchParrainages();
        } catch (err) {
            setError('Erreur lors de la désaffectation du parrainage');
            console.error(err);
        } finally {
            setDeaffectingId(null);
        }
    };

    return (
        <Card variant="outlined" className="mt-3">
            <Typography level="title-md" className="mb-2">
                Parrainages associés à {personneMorale?.raisonSociale}
            </Typography>

            <Divider />

            {loading ? (
                <Sheet className="flex justify-center p-4">
                    <AppLoading />
                </Sheet>
            ) : error ? (
                <Alert color="danger" className="my-2">
                    {error}
                </Alert>
            ) : (
                <>
                    {parrainages.length > 0 ? (
                        <Table stickyHeader className="w-full">
                            <thead>
                                <tr>
                                    <th className="w-1/6">Facture</th>
                                    <th className="w-1/6">Montant</th>
                                    <th className="w-1/6">Payé</th>
                                    <th className="w-1/6">Club</th>
                                    <th className="w-1/5">Collaborateur</th>
                                    <th className="w-1/12">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {parrainages.map((parrainage) => (
                                    <tr key={parrainage.id}>
                                        <td>
                                            {parrainage.facture.numeroFacture}
                                        </td>
                                        <td>
                                            {parrainage.montantPromesse} CHF
                                        </td>
                                        <td>
                                            {parrainage.facture.paiement
                                                ? `Oui (${parrainage.facture.montantPayeHorsTva} CHF)`
                                                : 'Non'}
                                        </td>
                                        <td>
                                            {parrainage.mandat?.club?.nom ||
                                                '-'}
                                        </td>
                                        <td>
                                            {parrainage.collaborateur
                                                ? `${parrainage.collaborateur.prenom} ${parrainage.collaborateur.nom}`
                                                : '-'}
                                        </td>
                                        <td>
                                            <Button
                                                color="danger"
                                                size="sm"
                                                onClick={() =>
                                                    handleDeaffect(
                                                        parrainage.id
                                                    )
                                                }
                                                disabled={
                                                    deaffectingId ===
                                                    parrainage.id
                                                }
                                                variant="soft"
                                            >
                                                {deaffectingId ===
                                                parrainage.id ? (
                                                    <AppLoading />
                                                ) : (
                                                    <>
                                                        <XMarkIcon className="h-4 w-4" />
                                                        Désaffecter
                                                    </>
                                                )}
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    ) : (
                        <Typography
                            level="body-md"
                            className="text-neutral-500 py-4 text-center"
                        >
                            Aucun parrainage associé à cette personne morale
                        </Typography>
                    )}
                </>
            )}

            <Snackbar
                open={!!success}
                variant="soft"
                color="success"
                autoHideDuration={3000}
                onClose={() => setSuccess(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                {success}
            </Snackbar>

            <Snackbar
                open={!!error}
                variant="soft"
                color="danger"
                autoHideDuration={3000}
                onClose={() => setError(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                {error}
            </Snackbar>
        </Card>
    );
};

export default ParrainagesPersonneMorale;
